import { Tabs } from "@base/ui/Tabs";
import { adminQueries } from "@features/admin/api/queryFactory";
import { AdminTabs } from "@features/admin/ui";
import { membershipQueries } from "@features/membership/api/queryFactory";
import Membership from "@features/membership/ui/Membership";
import { myinfoQueries } from "@features/myinfo/api/queryFactory";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Profile } from "./Profile";
import Reservations from "./Reservations";

const TAB_VALUES = ["profile", "membership", "reservations", "contact"] as const;
type TabValue = (typeof TAB_VALUES)[number];

interface TabConfig {
  value: TabValue;
  translationKey: string;
  renderContent: () => ReactNode;
  visible: boolean;
}

const TAB_CONFIGS: Omit<TabConfig, "visible">[] = [
  { value: "profile", translationKey: "profile", renderContent: () => <Profile /> },
  { value: "membership", translationKey: "membership", renderContent: () => <Membership /> },
  { value: "reservations", translationKey: "reservations", renderContent: () => <Reservations /> },
  // { value: "contact", translationKey: "contact", renderContent: () => <Contact /> },
];

export function MyInfoTabs() {
  const { t } = useTranslation();
  const {
    data: membership,
    isError: isMembershipError,
    isLoading: isMembershipLoading,
  } = useQuery(membershipQueries.membership());
  const {
    data: adminStatus,
    isError: isAdminStatusError,
    isLoading: isAdminStatusLoading,
  } = useQuery(adminQueries.status());
  // 과거 미팅룸 예약 이력이 1건이라도 있으면 예약 탭을 노출하기 위해 조회 (인증된 본인 이력만 반환됨)
  const { data: hasPastReservation } = useQuery(myinfoQueries.hasPastReservation());
  const [activeTab, setActiveTab] = useState<TabValue>("profile");
  const triggerRefs = useRef<Record<TabValue, HTMLButtonElement | null>>(
    {} as Record<TabValue, HTMLButtonElement | null>,
  );

  const isSilverPlan = !isMembershipError && membership?.plan_description?.type === "SILVER";
  const isReservationsTabVisible = isSilverPlan || hasPastReservation === true;

  const tabConfigs: TabConfig[] = useMemo(
    () =>
      TAB_CONFIGS.map((tab) => ({
        ...tab,
        visible: tab.value === "reservations" ? isReservationsTabVisible : true,
      })),
    [isReservationsTabVisible],
  );

  useEffect(() => {
    const activeTrigger = triggerRefs.current[activeTab];
    if (activeTrigger) {
      activeTrigger.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeTab]);

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabValue);
  };

  // Buildblock 관리자 계정은 프로필/멤버십/예약 내역 대신 관리자 탭을 본다.
  if (!isAdminStatusLoading && !isAdminStatusError && adminStatus?.admin === true) {
    return <AdminTabs />;
  }

  return (
    <Tabs
      layout="hug"
      color="primary"
      defaultValue={TAB_VALUES[0]}
      value={activeTab}
      onValueChange={(details) => handleTabChange(details.value)}
    >
      {isMembershipLoading ? null : (
        <div className="sticky top-[58px] z-10 -mx-4 px-4 bg-white md:static md:top-0 md:z-auto md:mx-0 md:px-0">
          <Tabs.List>
            {tabConfigs
              .filter((tab) => tab.visible)
              .map((tab) => (
                <Tabs.Trigger
                  key={tab.value}
                  value={tab.value}
                  className="px-4 py-2 pb-4 text-gray-500 whitespace-nowrap text-label2"
                  ref={(el) => {
                    triggerRefs.current[tab.value] = el;
                  }}
                >
                  {t(tab.translationKey)}
                </Tabs.Trigger>
              ))}
          </Tabs.List>
        </div>
      )}

      {tabConfigs
        .filter((tab) => tab.visible)
        .map((tab) => (
          <Tabs.Content key={tab.value} value={tab.value} className="py-8">
            {tab.renderContent()}
          </Tabs.Content>
        ))}
    </Tabs>
  );
}
